const runtimeOrigin = typeof window !== 'undefined' ? window.location.origin : '';
const envApiUrl = import.meta.env.VITE_API_URL?.trim();

const API_BASE = (() => {
  if (envApiUrl) {
    return envApiUrl.replace(/\/$/, '');
  }
  if (runtimeOrigin) {
    return `${runtimeOrigin}/api`;
  }
  return '/api';
})();

async function request(url, options = {}) {
  const token = localStorage.getItem('aic_token');
  const headers = { 'X-AIC-Client': 'studio', ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (options.body !== undefined && options.body !== null && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const res = await fetch(`${API_BASE}${url}`, { ...options, headers });

  if (res.status === 401) {
    localStorage.removeItem('aic_token');
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: { message: res.statusText } }));
    const e = new Error(err.error?.message || 'Request failed');
    e.code = err.error?.code;
    e.meta = err.error?.meta;
    e.status = res.status;
    throw e;
  }

  if (res.status === 204) return null;
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) return res.json();
  return res;
}

// Auth
export const api = {
  login: (email, password) =>
    request('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  register: (emailOrBody, maybePassword) => {
    const payload = typeof emailOrBody === 'object' && emailOrBody !== null
      ? emailOrBody
      : { email: emailOrBody, password: maybePassword };
    return request('/auth/register', { method: 'POST', body: JSON.stringify(payload) });
  },
  logout: () => request('/auth/logout', { method: 'POST' }),
  me: () => request('/me'),

  // Templates
  getTemplates: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/templates?${qs}`);
  },
  getTemplate: (id) => request(`/templates/${id}`),
  createTemplate: (data) => request('/templates', { method: 'POST', body: JSON.stringify(data) }),
  updateTemplate: (id, data) =>
    request(`/templates/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteTemplate: (id) => request(`/templates/${id}`, { method: 'DELETE' }),
  getTemplateVersions: (id) => request(`/templates/${id}/versions`),
  createTemplateVersion: (id, data = {}) =>
    request(`/templates/${id}/versions`, { method: 'POST', body: JSON.stringify(data) }),
  publishTemplate: (id) => request(`/templates/${id}/publish`, { method: 'POST' }),
  restoreTemplateVersion: (id, versionId) =>
    request(`/templates/${id}/versions/${versionId}/restore`, { method: 'POST' }),
  publishTemplateVersion: (id, versionId) =>
    request(`/templates/${id}/versions/${versionId}/publish`, { method: 'POST' }),

  // Assets
  getAssets: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/assets?${qs}`);
  },
  uploadAsset: (formData) =>
    request('/assets', { method: 'POST', body: formData, headers: {} }),
  deleteAsset: (id) => request(`/assets/${id}`, { method: 'DELETE' }),

  // Scripts
  getScripts: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/scripts?${qs}`);
  },
  getScript: (id) => request(`/scripts/${id}`),
  createScript: (data) => request('/scripts', { method: 'POST', body: JSON.stringify(data) }),
  updateScript: (id, data) =>
    request(`/scripts/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteScript: (id) => request(`/scripts/${id}`, { method: 'DELETE' }),

  // Folders
  getFolders: () => request('/folders'),
  createFolder: (data) => request('/folders', { method: 'POST', body: JSON.stringify(data) }),
  deleteFolder: (id) => request(`/folders/${id}`, { method: 'DELETE' }),

  // Jobs
  getJobs: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/jobs?${qs}`);
  },
  getJob: (id) => request(`/jobs/${id}`),
  cancelJob: (id) => request(`/jobs/${id}/cancel`, { method: 'POST' }),

  // Reports
  getReports: () => request('/reports'),
  downloadReport: (id) => request(`/reports/${id}`),
  getSystemStatus: () => request('/system/status', { headers: { 'Cache-Control': 'no-store' } }),

  // Render
  render: (body) => request('/render', { method: 'POST', body: JSON.stringify(body) }),
  renderAsync: (body) =>
    request('/render/async', { method: 'POST', body: JSON.stringify(body) }),
  renderPreview: async (body, options = {}) => {
    const includeDebugConsole = options.debugConsole === true;
    const token = localStorage.getItem('aic_token');
    const res = await fetch(`${API_BASE}/render/preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'X-AIC-Client': 'studio' },
      body: JSON.stringify({
        ...body,
        options: {
          ...(body?.options || {}),
          debugConsole: includeDebugConsole,
        },
      }),
    });
    if (!res.ok) {
      const errText = await res.text().catch(() => 'Preview request failed');
      try {
        const errJson = JSON.parse(errText);
        const e = new Error(errJson.error?.message || 'Preview request failed');
        e.code = errJson.error?.code;
        e.meta = errJson.error?.meta;
        e.status = res.status;
        throw e;
      } catch {
        const e = new Error(errText || 'Preview request failed');
        e.status = res.status;
        throw e;
      }
    }
    const ct = res.headers.get('content-type') || '';
    if (ct.includes('application/json')) {
      const payload = await res.json();
      return {
        html: payload?.html || '',
        consoleLogs: Array.isArray(payload?.consoleLogs) ? payload.consoleLogs : [],
      };
    }

    return {
      html: await res.text(),
      consoleLogs: [],
    };
  },

  // Tags
  getTags: () => request('/tags'),
  createTag: (data) => request('/tags', { method: 'POST', body: JSON.stringify(data) }),

  // Users (admin)
  getUsers: () => request('/users'),
  createUser: (data) => request('/users', { method: 'POST', body: JSON.stringify(data) }),
  updateUser: (id, data) => request(`/users/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteUser: (id) => request(`/users/${id}`, { method: 'DELETE' }),

  // Admin settings (branding)
  getAdminSettings: () => request('/admin/settings'),
  updateAdminSettings: (data) => request('/admin/settings', { method: 'PATCH', body: JSON.stringify(data) }),

  // i18n
  getCurrentLanguage: () => request('/languages/current'),
  getAdminLanguages: () => request('/admin/languages'),
  getAdminLanguage: (code) => request(`/admin/languages/${code}`),
  createAdminLanguage: (data) => request('/admin/languages', { method: 'POST', body: JSON.stringify(data) }),
  updateAdminLanguage: (code, data) => request(`/admin/languages/${code}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteAdminLanguage: (code) => request(`/admin/languages/${code}`, { method: 'DELETE' }),

  // Groups (admin)
  getGroups: () => request('/groups'),

  // Import/Export
  exportAll: () => {
    const token = localStorage.getItem('aic_token');
    return fetch(`${API_BASE}/export`, { headers: { Authorization: `Bearer ${token}`, 'X-AIC-Client': 'studio' } });
  },
  importBundle: (formData) =>
    request('/import', { method: 'POST', body: formData, headers: {} }),

  // ── Billing ──
  getPublicPlans: () => request('/public/plans'),
  getPublicLegalContent: () => request('/public/legal-content'),

  // Plans
  getBillingPlans: () => request('/billing/plans'),
  getBillingPlan: (id) => request(`/billing/plans/${id}`),
  createBillingPlan: (data) =>
    request('/billing/plans', { method: 'POST', body: JSON.stringify(data) }),
  updateBillingPlan: (id, data) =>
    request(`/billing/plans/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteBillingPlan: (id) =>
    request(`/billing/plans/${id}`, { method: 'DELETE' }),

  // Subscriptions
  getBillingSubscriptions: () => request('/billing/subscriptions'),
  createBillingSubscription: (data) =>
    request('/billing/subscriptions', { method: 'POST', body: JSON.stringify(data) }),
  updateBillingSubscription: (id, data) =>
    request(`/billing/subscriptions/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  confirmBillingPayment: (id) =>
    request(`/billing/subscriptions/${id}/confirm-payment`, { method: 'POST' }),
  cancelBillingSubscription: (id) =>
    request(`/billing/subscriptions/${id}`, { method: 'DELETE' }),

  // Billing settings
  getBillingSettings: () => request('/billing/settings'),
  updateBillingSettings: (data) =>
    request('/billing/settings', { method: 'PATCH', body: JSON.stringify(data) }),
  createStripeCheckoutSession: (data) =>
    request('/billing/checkout/session', { method: 'POST', body: JSON.stringify(data) }),

  // My usage
  getMyBilling: () => request('/billing/me'),
  getMyBillingPurchases: () => request('/billing/me/purchases'),

  // ── Dashboard ──
  getDashboardStats: () => request('/dashboard/stats'),

  // ── AI Generation ──
  getAISettings: () => request('/admin/ai-settings'),
  updateAISettings: (data) =>
    request('/admin/ai-settings', { method: 'PATCH', body: JSON.stringify(data) }),
  getAIProviders: () => request('/ai/providers'),
  aiGenerateTemplate: (prompt, generationMode = 'all') =>
    request('/ai/generate-template', { method: 'POST', body: JSON.stringify({ prompt, generationMode }) }),
  aiGenerateScript: (prompt) =>
    request('/ai/generate-script', { method: 'POST', body: JSON.stringify({ prompt }) }),
};
